import { useNavigate } from 'react-router-dom';
import { BIO_AGE_PATH, INFLAMMAGING_PATH } from '@/constants/paths';
import { Skeleton } from '@design-system/Skeleton';
import { useGetMyLongevityV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { MyLongevityResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import { getRankColor } from '@/utils/getViomeColor';
import {
  getBioAgeOverlayImage,
  getImflammingOverlayImage,
  getImflammingAnimationFromLocal,
  getBioAgeAnimationFromLocal,
  isBioAgeScoreHidden,
} from '@/utils/health/getAnimation';
import { LongvietyCards } from '@design-system/HealthSections/LongvietyCards';
import { EventTracker } from '@/utils';

export const LongevitySection = () => {
  const { data, isLoading, isError } = useGetMyLongevityV2();

  const navigate = useNavigate();

  if (isError) {
    return;
  }

  if (isLoading || !data) {
    return (
      <div className="mt-12">
        <Skeleton className="h-9 w-[50%] mb-4 rounded-lg" />
        <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-2 sm:grid-cols-2 mt-6">
          <Skeleton className="h-[24rem] w-full border border-stroke-static rounded-lg" />
          <Skeleton className="h-[24rem] w-full border border-stroke-static rounded-lg" />
        </div>
      </div>
    );
  }

  const { biological_age, inflammaging }: Partial<MyLongevityResponse> = data;

  return (
    <div className="mt-12" data-testid="health-longevity-section">
      <h2 className="dark typography-h1 text-primary mb-2" data-testid="health-longevity-title">My Longevity</h2>
      <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 sm:grid-cols-1 mt-6 place-items-center" data-testid="health-longevity-grid">
        {biological_age &&
          !isBioAgeScoreHidden(biological_age.biological_age) && (
          <LongvietyCards
            title="BioAge"
            isMainTextPercentage
            mainText={String(biological_age.biological_age)}
            subText={biological_age.message}
            overlayImg={getBioAgeOverlayImage(
              biological_age.biological_age,
              biological_age.actual_age,
            )}
            videoSrc={getBioAgeAnimationFromLocal(
              biological_age.biological_age,
              biological_age.actual_age,
            )}
            additionalText={`Actual Age ${biological_age.actual_age}`}
            footer="A look into how you're aging from within"
            videoHeight="h-[26rem]"
            data-testid="health-longevity-bioAgeCard"
            onClick={() => {
              EventTracker.trackEvent('health', 'health_myLongevity_bioAge');
              navigate(`${BIO_AGE_PATH}`);
            }}
          />
        )}
        {inflammaging && (
          <LongvietyCards
            title="Inflammaging"
            mainText={inflammaging.message}
            subText={inflammaging.ranking}
            subTextColor={getRankColor(inflammaging.ranking)}
            overlayImg={getImflammingOverlayImage(inflammaging.message)}
            videoSrc={getImflammingAnimationFromLocal(inflammaging.message)}
            footer="Understand how chronic inflammation and stress impact your health and aging."
            videoHeight="h-[24rem]"
            inflammagingAddonClasses={{ relativeTop: 'top-[18px]', videoFrameHeight: 'h-[26rem]' }}
            data-testid="health-longevity-inflammagingCard"
            onClick={() => {
              EventTracker.trackEvent(
                'health',
                'health_myLongevity_inflammaging',
              );
              navigate(`${INFLAMMAGING_PATH}`);
            }}
          />
        )}
      </div>
    </div>
  );
};
